import { useEffect, useRef } from 'react';
import { CheckIcon, WarningIcon } from '@phosphor-icons/react';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { fmtMoneyWhole } from '../results/flight_format.js';
import type { FlightFareSource, FlightTariffSource } from './fare_source.js';
import type { FaresText, FlightFaresCopy } from './fares_copy.js';
import { resolveLadderColumnLabel, type LegCarriers } from './ladder_labels.js';
// A aritmética da seleção (preço da combinação, se ela fecha, bagagem
// assimétrica) é COMPARTILHADA com a tela "Escolha as tarifas de cada voo"
// (#2262) e mora em `fare_selection.ts`. Aqui fica só a casca: rótulos,
// avisos, rodapé e CTA em volta dos trilhos de cards.
import {
  asymmetricChecked,
  comboFares,
  hasReturnLadder,
  summarizeSelection,
  type FareSel,
} from './fare_selection.js';
import { FareRail } from './fare_rail.js';
import './fare_expansion.css';

/** Estado do fetch do tariff (cacheado por oferta no `useOfferFares`).
 *  `error` é transitório (retry pode resolver); `stale` é handle morto na
 *  sessão modal (409 `offer_stale`, #2332) — retry NUNCA resolve, o caminho é
 *  re-buscar. `leg_unavailable` (#6000): a companhia tarifou só a outra perna
 *  do par — re-buscar devolve o mesmo voo sem tarifa, o caminho é trocar o voo
 *  de `leg`. */
export type TariffFetchState<V extends FlightTariffSource = FlightTariffSource> =
  | { phase: 'loading' }
  | { phase: 'ready'; view: V }
  | { phase: 'error' }
  | { phase: 'stale' }
  | { phase: 'leg_unavailable'; leg: 'outbound' | 'return' };

/** De que trilho é o rótulo — a perna, ou o par de combos. */
type RailKind = 'outbound' | 'return' | 'bundled';

interface RailSpec {
  /** Cabeçalho do trilho — nomeia a LISTA que vem abaixo. A legenda da pilha
   *  no rodapé sai do mesmo `kind`, mas nomeia o que a tarifa escolhida COBRE:
   *  no bundled a lista é "Tarifas · ida + volta" mas a pilha diz "Ida e
   *  volta", que é o trecho, não o título da lista. */
  kind: RailKind;
  fares: readonly FlightFareSource[];
  sel?: string;
  pick: (fareId: string) => void;
}

/** Os trilhos do painel. Bundled é UM trilho de combos: a companhia tarifa o par
 *  junto, então oferecer dois trilhos independentes deixaria montar combinação
 *  que não existe. Split: um trilho por perna, a volta só quando o tariff a traz. */
function buildRails(
  view: FlightTariffSource,
  combos: readonly FlightFareSource[],
  sel: FareSel,
  onPick: (patch: Partial<FareSel>) => void,
): RailSpec[] {
  if (combos.length > 0) {
    return [
      {
        kind: 'bundled',
        fares: combos,
        sel: sel.bundled,
        pick: (id) => {
          onPick({ bundled: id });
        },
      },
    ];
  }
  const specs: RailSpec[] = [
    {
      kind: 'outbound',
      fares: view.departing,
      sel: sel.outbound,
      pick: (id) => {
        onPick({ outbound: id });
      },
    },
  ];
  if (hasReturnLadder(view)) {
    specs.push({
      kind: 'return',
      fares: view.returning,
      sel: sel.return,
      pick: (id) => {
        onPick({ return: id });
      },
    });
  }
  return specs;
}

/** Um trilho de cards por perna, cada um com o rótulo resolvido em cima (o mesmo
 *  texto é o nome acessível do `radiogroup`, dentro do `FareRail`). Componente
 *  irmão e não bloco inline: o `FareExpansion` está no teto de complexidade. */
function FareRails({
  rails,
  labelOf,
  readOnly,
  text,
}: {
  readonly rails: RailSpec[];
  readonly labelOf: (rail: RailSpec, index: number) => string;
  readonly readOnly: boolean;
  readonly text: FaresText;
}) {
  return (
    <div className="fqx-rails">
      {rails.map((rail, i) => {
        const label = labelOf(rail, i);
        return (
          <section key={rail.kind} className="fqx-rail">
            <span className="fqx-rail-label">{label}</span>
            <FareRail
              label={label}
              fares={rail.fares}
              selected={rail.sel}
              {...(readOnly ? { readOnly } : {})}
              onPick={rail.pick}
              text={text}
            />
          </section>
        );
      })}
    </div>
  );
}

/** O que a combinação escolhida É, ao lado do total. Não aglutina quando as
 *  tarifas coincidem: a faixa tem UMA forma, e quem aprendeu "a segunda pilha é
 *  a volta" não pode perder a referência justamente quando as pernas batem.
 *
 *  `dl` e não spans: sem o par termo/definição o leitor de tela despeja
 *  "Ida Light Volta Classic" como uma frase só, e aí a pilha perde exatamente o
 *  pareamento que ela existe pra criar. */
function CombinationName({ legs }: { readonly legs: { caption: string; name: string }[] }) {
  if (legs.length === 0) return null;
  return (
    <dl className="fqx-combo">
      {legs.map((l) => (
        <div key={l.caption} className="fqx-combo-leg">
          <dt className="fqx-combo-cap">{l.caption}</dt>
          <dd className="fqx-combo-v">{l.name}</dd>
        </div>
      ))}
    </dl>
  );
}

/** O modo do CTA do rodapé — ver a doc da prop `cta` do `FareExpansion`. */
export interface FareExpansionCta {
  mode: 'confirm';
  disabled?: boolean;
  /** O HOST está processando o pick (a bancada cotando a seleção): o CTA
   *  fica em cena, travado e girando, mesmo com a expansão `readOnly` — o
   *  clique precisa de resposta onde foi dado, não numa faixa que a tela cheia
   *  do celular cobre (validação de 30/09). */
  busy?: boolean;
  /** O verbo do host no modo confirm. Ausente ⇒ "Confirmar" (a escada inline
   *  do explorer e da gaveta); a página "Escolha as tarifas" passa o dela —
   *  "Ir para checkout" na bancada, "Confirmar" pelo "Ver todos" da conversa. */
  label?: string;
}

/**
 * A cópia do CTA do rodapé — extraída (mesmo padrão de `cabinSeam`/
 * `pillNodeFor` no explorer) porque o `FareExpansion` já está no teto de
 * complexidade. Modo `cta` (o rodapé fecha a viagem, ver a doc da prop no
 * `FareExpansion`): o `label` do host, senão "Confirmar". Ausente: "Próximo"
 * (decisão de 29/09, #3875) — "Selecionar" já é o verbo de cada cartão de
 * tarifa acima, e no chat o pick vai ao agente e a conversa segue; a mesma
 * chave da tela cheia do celular.
 */
function footCtaCopy(cta: FareExpansionCta | undefined, copy: FlightFaresCopy): string {
  if (!cta) return copy.next;
  return cta.label ?? copy.confirm;
}

/** O botão do rodapé. Fora do `FareExpansion` pelo teto de complexidade dele.
 *  `busy` (o host processando o pick) trava e gira no lugar do ✓. */
function FootCta({
  cta,
  copy,
  disabled,
  onClick,
}: Readonly<{
  cta: FareExpansionCta | undefined;
  copy: string;
  disabled: boolean;
  onClick: () => void;
}>) {
  const busy = Boolean(cta?.busy);
  return (
    <button
      type="button"
      className="fui-btn fui-btn-primary fqx-cta"
      disabled={disabled || Boolean(cta?.disabled) || busy}
      aria-busy={busy || undefined}
      onClick={onClick}
    >
      {/* Só o modo `cta` leva glifo — o ✓ do Confirmar do header (mesmo
          ícone, mesmo verbo). "Selecionar" vai sem ícone: o `+` que morava
          aqui era o sinal de carrinho que este rodapé deixou de dar, e ele não
          tem substituto porque o rótulo é uma palavra só em qualquer largura. */}
      {cta && (busy ? <Spinner size={16} aria-hidden /> : <CheckIcon size={16} aria-hidden />)}
      {copy}
    </button>
  );
}

/**
 * Expansão inline de tarifas de UMA oferta (abaixo da linha): a mesma
 * apresentação da tela "Escolha as tarifas de cada voo" — trilhos de cards de
 * família, um por perna (bundled = trilho único de combos) — dentro da casca
 * que a linha já tinha: rótulo com a companhia de marketing, aviso de bagagem
 * assimétrica, total, combinação e o CTA que devolve a seleção pro
 * `OfferListBody` (o `flight_offers` despacha `select_offer_fares`).
 */
/** A faixa do estado `stale` (handle morto, #2332): copy de expiração e, quando
 *  o host tem como re-buscar, o CTA "Buscar de novo". `onResearch` é required-
 *  possivelmente-undefined (e não opcional) de propósito: o `FareExpansion`
 *  repassa o que recebeu sem ganhar um ternário — o teto de complexidade dele
 *  está no limite. `researching` (#5277): a busca refeita está em voo — o CTA
 *  fica travado e diz que ela está andando, em vez de abrir um segundo turno.
 *  A perna sem tarifa (#6000) só avisa: re-buscar devolveria o mesmo voo, e a
 *  troca de voo é da tela "Escolha as tarifas", não da linha do card. */
function StaleFaresBand({
  state,
  onResearch,
  researching,
  copy,
}: {
  readonly state: Extract<TariffFetchState, { phase: 'stale' | 'leg_unavailable' }>;
  readonly onResearch: (() => void) | undefined;
  readonly researching: boolean | undefined;
  readonly copy: FlightFaresCopy;
}) {
  if (state.phase === 'leg_unavailable') {
    return (
      <div className="fqx fqx-error" role="alert">
        <span>{copy.faresLegUnavailable(state.leg)}</span>
      </div>
    );
  }
  return (
    <div className="fqx fqx-error" role="alert">
      <span>{copy.faresStale}</span>
      {onResearch && (
        <button type="button" className="fqx-retry" disabled={researching} onClick={onResearch}>
          {researching ? copy.searchingAgain : copy.searchAgain}
        </button>
      )}
    </div>
  );
}

/**
 * Traz o rodapé (`Total` + `Selecionar`) à vista quando a escada TERMINA de abrir.
 *
 * A altura da escada só é conhecida depois que as tarifas chegam — e nesse momento
 * a thread do chat já parou de rolar (o card chega auto-expandido e cresce
 * sozinho), então o CTA nascia abaixo da dobra, embaixo do dock do composer
 * (QA 2026-09-12). Quem sabe o tamanho é a própria escada.
 *
 * `block: 'nearest'` = rolagem MÍNIMA: rodapé já inteiro em cena não move nada, e
 * uma escada aberta no meio da conversa não arranca ninguém da posição. O respiro
 * contra o composer vem do `scroll-padding-bottom` do scroller do chat — a régua é
 * do host, não do kit.
 *
 * `readOnly` (kanban/operador, checkout) não tem CTA: sem nada a alcançar, sem
 * rolagem. O guard de `scrollIntoView` cobre jsdom sem o método.
 */
function useFootInView(
  phase: TariffFetchState['phase'],
  readOnly: boolean,
): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    // The foot only exists on `ready`: it is the ladder that finished opening.
    if (phase !== 'ready' || !el || readOnly || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ block: 'nearest' });
  }, [phase, readOnly]);
  return ref;
}

export function FareExpansion({
  state,
  sel,
  readOnly,
  soloLegLabel,
  legCarriers,
  cta,
  onPick,
  onSelect,
  onRetry,
  onResearch,
  researching,
  text,
}: {
  readonly state: TariffFetchState;
  readonly sel: FareSel;
  readonly readOnly?: boolean;
  /** Nome da perna quando o trilho é ÚNICO e não é o par de um ida-e-volta —
   *  o roteiro multi-trecho passa "Trecho N". Ausente ⇒ "Ida", que é o certo no
   *  card binário. */
  readonly soloLegLabel?: string;
  /** Companhia de MARKETING de cada perna (índice 0 = ida, 1 = volta) — nomeia
   *  o trilho porque é dela a família que a lista mostra (DL 6057 operado pela
   *  LATAM vende famílias Delta; sem o nome, quem lê assume a cia errada e acha
   *  o trilho errado). Ausente no bundled: o par de combos não é de UMA
   *  companhia, então o rótulo do combo não nomeia nenhuma. */
  readonly legCarriers?: LegCarriers;
  /**
   * Presente quando este pick é o ÚLTIMO trecho pendente do explorer — o
   * rodapé troca "Selecionar" por "Confirmar" (o mesmo verbo do Confirmar do
   * header) e o clique fecha a seleção no mesmo gesto, sem CTA extra em outro
   * lugar da tela. Ausente ⇒ o modo de hoje: os cards do chat
   * (`flight_offers`/`flight_multi_offers`) nunca passam esta prop, e o CTA
   * segue "Selecionar" — a expansão é COMPARTILHADA entre eles e o explorer, e
   * este é o único jeito de diferenciar o rodapé sem duplicá-la.
   * `disabled` é o draftLock do explorer (faixa com edição não buscada): a
   * tela nunca pode confirmar um pedido que ela mesma não está mostrando.
   * A página "Escolha as tarifas" (desenho largo) monta ESTE corpo e passa o
   * `label` do host — é o que faz a página e a expansão do chat serem a mesma
   * peça (padronização de 30/09, #3875).
   */
  readonly cta?: FareExpansionCta;
  readonly onPick: (patch: Partial<FareSel>) => void;
  readonly onSelect: (data: { total: number; currency: string; names: string }) => void;
  readonly onRetry: () => void;
  /** Recuperação do estado `stale` (handle morto, #2332): dispara uma NOVA busca
   *  — re-tentar a mesma oferta repete o 409 para sempre. Ausente ⇒ o host não
   *  tem como re-buscar, e a faixa mostra só a copy, sem CTA morto. */
  readonly onResearch?: () => void;
  /** A re-busca do `onResearch` está em voo (#5277) — ver `StaleFaresBand`. */
  readonly researching?: boolean;
  readonly text: FaresText;
}) {
  const { copy } = text;
  const footRef = useFootInView(state.phase, Boolean(readOnly));

  if (state.phase === 'loading') {
    return (
      <output className="fqx fqx-skel">
        <span className="fqx-loading">{copy.loadingFares}</span>
        <span className="fqx-bar" />
        <span className="fqx-bar" />
        <span className="fqx-bar" />
      </output>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="fqx fqx-error" role="alert">
        <span>{copy.faresError}</span>
        <button type="button" className="fqx-retry" onClick={onRetry}>
          {copy.tryAgain}
        </button>
      </div>
    );
  }
  if (state.phase !== 'ready') {
    return (
      <StaleFaresBand state={state} onResearch={onResearch} researching={researching} copy={copy} />
    );
  }

  const view = state.view;
  const combos = comboFares(view);

  const rails = buildRails(view, combos, sel, onPick);

  // Trilho único e não-bundled = UMA perna. Só num binário essa perna é "a ida";
  // num roteiro multi-trecho ela é o trecho da vez, e o host manda o número.
  const solo = soloLegLabel && rails.length === 1 && combos.length === 0 ? soloLegLabel : null;
  // A decisão do rótulo (solo vence, bundled não sofre, senão a companhia de
  // MARKETING desambigua o par) é PURA e mora em `ladder_labels.ts` — aqui é só
  // encadear a prop.
  const railTitle = { outbound: copy.outbound, return: copy.return, bundled: copy.faresBundled };
  const railFoot = { outbound: copy.outbound, return: copy.return, bundled: copy.bothLegs };
  const labelOf = (rail: RailSpec, i: number): string =>
    resolveLadderColumnLabel({
      base: railTitle[rail.kind],
      carrier: legCarriers?.[Math.min(i, 1)],
      solo,
      bundled: combos.length > 0,
    });
  const footOf = (rail: RailSpec): string => solo ?? railFoot[rail.kind];

  const { total, currency, names, complete, legs } = summarizeSelection(view, combos, sel);

  // Pilhas do rodapé: uma por perna, com o nome da tarifa escolhida nela.
  const comboLegs = rails
    .map((rail) => ({
      caption: footOf(rail),
      name: rail.fares.find((f) => f.fareId === rail.sel)?.name ?? '',
    }))
    .filter((l) => l.name !== '');

  const asymmetric = asymmetricChecked(legs, text.classify);
  const ctaCopy = footCtaCopy(cta, copy);

  return (
    <div className="fqx">
      <FareRails rails={rails} labelOf={labelOf} readOnly={Boolean(readOnly)} text={text} />
      {asymmetric && (
        // Âmbar e não vermelho: a combinação é comprável, só merece um segundo
        // olhar. Vermelho, dentro deste painel, quer dizer ausência de benefício.
        <p className="fqx-warn" role="note">
          <WarningIcon size={14} weight="fill" aria-hidden />
          <span>
            {/* O artigo é interpolado porque com multi-trecho o rótulo deixa de
                ter gênero previsível (`a ida`, `a volta`, mas `o trecho 2`).
                Hoje as duas pernas do aéreo são femininas; quando chegar
                "Trecho N" o artigo passa a sair do próprio rótulo. */}
            {/* A chave sai do KIND: "sem despacho" e "despacha mediante taxa"
                são avisos diferentes, e chamar taxa de ausência é mentir na
                direção que assusta. */}
            {copy.asymmetric(asymmetric.kind, asymmetric.leg)}
          </span>
        </p>
      )}
      <div className="fqx-foot" ref={footRef}>
        <span className="fqx-total">
          {/* Só `Total`. Com pernas independentes a combinação pode ser
              Light + Top, e aí "Total · Classic" mentiria. */}
          <span className="fqx-total-cap">{copy.total}</span>
          <span className="fqx-total-v">{fmtMoneyWhole({ amount: total, currency })}</span>
        </span>
        <CombinationName legs={comboLegs} />
        {(!readOnly || cta?.busy) && (
          <FootCta
            cta={cta}
            copy={ctaCopy}
            disabled={!complete}
            onClick={() => {
              onSelect({ total, currency, names });
            }}
          />
        )}
      </div>
    </div>
  );
}
